import { snakeCase } from 'lodash';
import React from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ArrowUpRightCircle, BoxArrowUpRight, FileEarmarkCheck, InfoCircle, PersonCircle } from 'react-bootstrap-icons';
import {
  ADMIN_ROLE,
  ALL_ROLES,
  APARTMENT_STATUS,
  APARTMENT_STATUS_LABELS,
  COMPLICATED_DEALS_OPTIONS,
  DEFAULT_DATE_FORMAT,
  IRR_CALCULATOR_KEY,
  MANAGER_ROLE,
  PAPER_CALCULATOR_KEY,
  PINUI_BINUI_CALCULATOR_KEY,
  profileAssignmentStatusOptions,
  PROGRAM_TYPES,
  rangeOptions,
  REAL_ESTATE_ANALYST_ROLE,
  RSU_OPTIONS_KEY,
  SALES_ROLE,
  STATUS_FREE_IRR_CALC,
  STATUS_INTEREST_IN_MY_COURSE,
  CUSTOMER_LEVEL_OPTIONS
} from './constants';
import { apartmentTypes, isAdmin } from './global';
import cities from './israel-cities.json';
import moment from 'moment';
import { ApartmentColEditStatusDropdown } from '../components/appartment/ApartmentColEditStatusDropdown';
import EditAppartmentDates from '../components/appartment/EditAppartmentDates';
import { toast } from 'react-toastify';
import FormErrorMessage from 'rsuite/esm/FormErrorMessage';
import { formatPercentage } from '../components/admin/manage-users/customer/strategies/helper';

export const GROUPPED_USER_BASED_ON_ROLE = ({ users = [], priotize = SALES_ROLE, translate }) => {
  const rolesToShowAtTop = [priotize, ADMIN_ROLE, MANAGER_ROLE];

  const sortedRole = [...rolesToShowAtTop, ...ALL_ROLES.filter(r => !rolesToShowAtTop.includes(r))];

  const activeUsers = users.filter(u => !u.archived);
  const archivedUsers = users.filter(u => u.archived);

  const roleGroups = sortedRole
    .map(role => ({
      label: role,
      key: role,
      options: activeUsers
        .filter(u => u.role === role)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map(user => ({
          label: user.name || '',
          value: user._id
        }))
    }))
    .filter(group => group.options.length > 0);

  const archivedGroup = {
    label: translate('archived'),
    key: 'archived',
    options: archivedUsers
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(user => ({
        label: user.name || '',
        value: user._id
      }))
  };

  return archivedUsers.length > 0 ? [...roleGroups, archivedGroup] : roleGroups;
};

export const customerFormFields = (translate, user, users, crmContact) => {
  return [
    {
      key: 'name',
      label: 'name',
      type: 'text',
      cellSizeMD: 4,
      hide: Boolean(crmContact)
    },
    {
      key: 'phone',
      label: 'Phone Number',
      type: 'text',
      cellSizeMD: 4,
      hide: Boolean(crmContact)
    },
    {
      key: 'email',
      label: 'Email',
      type: 'text',
      cellSizeMD: 4,
      hide: Boolean(crmContact),
      errorMessage: errorMessages => {
        const error = errorMessages?.find(e => e?.key === 'email');
        return error ? (
          <div
            onClick={() => {
              error?.onClick();
            }}
            className="text-danger mb-0 mt-1 tiny d-flex align-items-center gap-1 hover"
          >
            <span>{error?.message}</span> <BoxArrowUpRight size={10} />
          </div>
        ) : null;
      }
    },

    {
      key: 'secondaryName',
      label: 'Secondary Name',
      type: 'text',
      cellSizeMD: 4,
      hide: Boolean(crmContact)
    },
    {
      key: 'secondaryPhone',
      label: 'Secondary Phone Number',
      type: 'text',
      cellSizeMD: 4,
      hide: Boolean(crmContact)
    },
    {
      key: 'secondaryEmail',
      label: 'Secondary Email',
      type: 'text',
      cellSizeMD: 4,
      hide: Boolean(crmContact)
    },

    {
      key: 'address',
      label: 'Address',
      type: 'text',
      cellSizeMD: 12,
      hide: Boolean(crmContact)
    },
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'New', label: translate('new') },
        { value: 'Active', label: translate('active') },
        { value: 'Past', label: translate('past') },
        { value: STATUS_INTEREST_IN_MY_COURSE, label: translate('interested_in_my_course') },
        { value: STATUS_FREE_IRR_CALC, label: STATUS_FREE_IRR_CALC }
      ],
      cellSizeMD: 4,
      type: 'dropdown',
      hideTitle: false
    },
    {
      key: 'programType',
      label: 'Program Type',
      type: 'dropdown',
      options: PROGRAM_TYPES?.map(pT => ({ value: pT, label: translate(snakeCase(pT)) || pT })),
      clearableOption: true,
      //   clearableOptionTitle:'Select One',
      cellSizeMD: 4
    },
    {
      key: 'level',
      label: 'Level',
      type: 'select',
      isMultiSelect: false,
      options: CUSTOMER_LEVEL_OPTIONS.map(option => ({
        ...option,
        label: translate(snakeCase(option.label)) || option.label
      })),
      optionRenderer: option => (
        <span className="d-flex align-items-center gap-2">
          {option.label}
          {option.description && (
            <OverlayTrigger
              placement="top"
              delay={{ show: 150, hide: 300 }}
              container={document.body}
              popperConfig={{
                strategy: 'fixed',
                modifiers: [
                  { name: 'zIndex', options: { zIndex: 10000000000 } },
                  { name: 'flip', enabled: false },
                  { name: 'offset', options: { offset: [0, 6] } }
                ]
              }}
              overlay={props => (
                <Tooltip
                  id={`level-tooltip-${option.value}`}
                  {...props}
                  style={{ ...props.style, zIndex: 10000000000 }}
                >
                  {option.description}
                </Tooltip>
              )}
            >
              <span onClick={e => e.stopPropagation()} role="presentation">
                <InfoCircle size={12} className="text-muted" style={{ cursor: 'help', flexShrink: 0 }} />
              </span>
            </OverlayTrigger>
          )}
        </span>
      ),
      cellSizeMD: 4
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date',
      cellSizeMD: 4
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'date',
      cellSizeMD: 4,
      min: moment()
        .add(1, 'day')
        .format('YYYY-MM-DD')
    },
    {
      key: 'levelTo',
      label: 'Level To',
      type: 'select',
      isMultiSelect: false,
      options: data =>
        CUSTOMER_LEVEL_OPTIONS.map(option => ({
          ...option,
          label: translate(snakeCase(option.label)) || option.label,
          isDisabled: Number(data?.level) === Number(option.value)
        })),
      optionRenderer: option => (
        <span className="d-flex align-items-center gap-2">
          {option.label}
          {option.description && (
            <OverlayTrigger
              placement="top"
              delay={{ show: 150, hide: 300 }}
              container={document.body}
              popperConfig={{
                strategy: 'fixed',
                modifiers: [
                  { name: 'zIndex', options: { zIndex: 10000000000 } },
                  { name: 'flip', enabled: false },
                  { name: 'offset', options: { offset: [0, 6] } }
                ]
              }}
              overlay={props => (
                <Tooltip
                  id={`level-to-tooltip-${option.value}`}
                  {...props}
                  style={{ ...props.style, zIndex: 10000000000 }}
                >
                  {option.description}
                </Tooltip>
              )}
            >
              <span onClick={e => e.stopPropagation()} role="presentation">
                <InfoCircle size={12} className="text-muted" style={{ cursor: 'help', flexShrink: 0 }} />
              </span>
            </OverlayTrigger>
          )}
        </span>
      ),
      cellSizeMD: 4
    },
    {
      key: 'logsCreatedFromEndDateTriggerLogWarningMessage',
      type: 'custom-render',
      hideTitle: true,
      cellSizeMD: 12,
      render: data => {
        const logsCreatedFromEndDateTrigger = data?.logCreatedFromEndDateTrigger || [];
        if (logsCreatedFromEndDateTrigger.length === 0) return null;
        const latestLog = logsCreatedFromEndDateTrigger.sort(
          (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
        )[0];

        const message = translate('user_level_changed_from_to_on', {
          levelFrom: latestLog.levelFrom,
          levelTo: latestLog.levelTo,
          date: moment(latestLog.endDate).format('ddd MMM DD YYYY')
        });
        return (
          <div className="alert alert-warning py-2 small w-100 mt-1 mb-0" role="status">
            {message}
          </div>
        );
      }
    },
    {
      key: 'additionalInfo',
      label: 'Additional Information',
      type: 'text',
      cellSizeMD: 12
    },

    {
      key: 'allowedCalculators',
      label: 'Allowed Calculators',
      type: 'groupped-checkbox',
      options: [
        {
          label: translate(snakeCase('Irr')),
          value: IRR_CALCULATOR_KEY
        },
        {
          label: translate(snakeCase('Pinui Binui')),
          value: PINUI_BINUI_CALCULATOR_KEY
        },
        {
          label: translate(snakeCase('Paper Apartment')),
          value: PAPER_CALCULATOR_KEY
        },
        {
          label: translate(snakeCase('RSU Options')),
          value: RSU_OPTIONS_KEY
        }
      ],
      cellSizeMD: 12
    },
    {
      key: 'showRujumBoard',
      title: 'rujum',
      label: translate(snakeCase('show_rujum')),
      type: 'checkbox',
      cellSizeMD: 4
    },
    {
      key: 'showGeneralInfo',
      label: 'General Information',
      type: 'checkbox',
      cellSizeMD: 4
    },
    {
      key: 'isPublic',
      label: 'Example user',
      type: 'checkbox',
      cellSizeMD: 4,
      renderExtraLabel: customer => {
        return customer?.isPublic && customer?._id ? (
          <Badge className="mx-1" bg={'dark'}>
            <a
              className="text-white text-decoration-none d-flex align-items-center"
              href={`${process.env.REACT_APP_APP_URL}/example/${customer?._id}`}
              target="_blank"
            >
              <span className="tiny">{translate('open')}</span> <ArrowUpRightCircle size={10} className="mx-1" />
            </a>
          </Badge>
        ) : (
          ''
        );
      }
    },
    {
      key: 'assignedTo',
      label: translate('assigned_to'),
      type: 'grouped-dropdown',
      options: GROUPPED_USER_BASED_ON_ROLE({ users, translate }),
      maxItemCustomMessage: length => `${length} user${length === 1 ? '' : 's'} selected`,
      cellSizeMD: 4,
      hide: !isAdmin(user?.role) || crmContact,
      isMultiSelect: true,
      isGrouped: true
    }
  ]?.filter(f => !f?.hide);
};

export const editCrmContactDetailsFields = (users, translate) => {
  return [
    {
      key: 'firstName',
      label: 'First Name',
      cellSizeMD: 4
    },
    {
      key: 'lastName',
      label: 'Last Name',
      cellSizeMD: 4
    },
    {
      key: 'address',
      label: 'Address',
      type: 'text',
      cellSizeMD: 4
    },
    {
      key: 'phoneNumbers',
      label: 'phone_numbers',
      type: 'phone',
      cellSizeMD: 6,
      isMultiple: true
    },
    {
      key: 'emails',
      label: 'Emails',
      type: 'email',
      cellSizeMD: 6,
      isMultiple: true,
      errorMessage: errorMessages => {
        const error = errorMessages?.find(e => e?.key === 'email');
        return error ? (
          <div
            onClick={() => {
              error?.onClick();
            }}
            className="text-danger mb-0 mt-1 tiny d-flex align-items-center gap-1 hover"
          >
            <span>{error?.message}</span> <BoxArrowUpRight size={10} />
          </div>
        ) : null;
      }
    },
    {
      key: 'assignedTo',
      label: translate('assigned_to'),
      type: 'grouped-dropdown',
      options: GROUPPED_USER_BASED_ON_ROLE({ users, translate }),
      cellSizeMD: 4,
      isMultiSelect: true,
      isGrouped: true
    }
  ];
};

export const editMultipleApartmentsFields = ({ users, translate }) => {
  return [
    {
      key: 'status',
      label: translate('status'),
      type: 'select',
      options: APARTMENT_STATUS.filter(s => !['preparing_contract', 'bought_by_our_client'].includes(s))?.map(s => ({
        label: translate(s),
        value: APARTMENT_STATUS_LABELS[s]
      })),
      cellSizeMD: 3,
      isMultiSelect: false,
      isGrouped: false
    },
    {
      key: 'realEstateAnalyst',
      label: translate('real_estate_analyst'),
      type: 'grouped-dropdown',
      options: GROUPPED_USER_BASED_ON_ROLE({ users, priotize: REAL_ESTATE_ANALYST_ROLE, translate }),
      cellSizeMD: 3,
      isMultiSelect: false
    },
    {
      key: 'isApartmentArchived',
      label: `${translate('archive') || 'Archive'} / ${translate('unarchive') || 'Unarchive'}`,
      type: 'radio-checkbox',
      options: [
        { label: translate('archive') || 'Archive', value: true },
        { label: translate('unarchive') || 'Unarchive', value: false }
      ],
      cellSizeMD: 3,
      isMultiSelect: false,
      isGrouped: false
    }
  ];
};

export const apartmentTableColumns = ({
  translate,
  users = [],
  setSelectedApartment,
  selectedApartment,
  selectAllMode,
  onToggleSelectAll,
  onToggleSelectItem,
  isItemSelected,
  showMoreStatuses
}) => {
  let rangeOpt = [...rangeOptions?.map(o => ({ label: o, value: o }))];
  let yesNoOpt = ['Yes', 'No']?.map(o => ({ label: o, value: o }));
  const filteredStatuses = showMoreStatuses
    ? APARTMENT_STATUS
    : APARTMENT_STATUS.filter(s => !['bought_by_outsider', 'irrelevant'].includes(s));

  let statusSearchOptions = filteredStatuses.map(s => ({ label: translate(s), value: APARTMENT_STATUS_LABELS[s] }));
  const toggleText = showMoreStatuses ? translate('show_less') : translate('show_more');
  statusSearchOptions = [
    ...statusSearchOptions,
    {
      label: toggleText,
      value: showMoreStatuses ? 'Show less...' : 'Show more...',
      isToggle: true
    }
  ];

  const allLabels = [
    {
      key: 'view',
      labelRenderer: field => (
        <input
          type="checkbox"
          className="hover"
          checked={selectAllMode}
          onChange={e => {
            e.stopPropagation();
            onToggleSelectAll(e.target.checked);
          }}
        />
      ),
      cellRenderer: apartment => (
        <div style={{ height: '100%' }}>
          <input
            type="checkbox"
            className="hover"
            checked={isItemSelected(apartment)}
            onClick={e => {
              e.stopPropagation();
              onToggleSelectItem(apartment);
            }}
          />
        </div>
      ),
      onCellClick: apartment => onToggleSelectItem(apartment),
      disableSort: true,
      disableSearch: true,
      noInternalCellPadding: true,
      width: 50
    },
    {
      key: 'createdAt',
      label: 'Created at',
      type: 'date',
      width: 150
    },
    {
      key: 'realEstateAnalyst',
      label: translate('real_estate_analyst'),
      type: 'grouped-dropdown',
      valueSelector: app => users?.find(u => u._id === app?.realEstateAnalyst)?.name || '-',
      searchOptions: GROUPPED_USER_BASED_ON_ROLE({ users, priotize: REAL_ESTATE_ANALYST_ROLE, translate }),
      width: 150,
      isGroupedDropdown: true,
      isMultiSelect: true
    },
    {
      key: 'city',
      label: translate('city'),
      type: 'text',
      searchOptions: cities.map(c => ({ label: c.city, value: c.city })),
      isMultiSelect: true,
      maxItemCustomMessage: length => `${length} ${length > 1 ? 'cities' : 'city'}`,
      width: 150
    },
    {
      key: 'address',
      label: translate('address'),
      type: 'text',
      width: 150,
      disableSort: true
    },
    {
      key: 'status',
      label: translate('status'),
      type: 'text',
      isMultiSelect: true,
      width: 180,
      searchOptions: statusSearchOptions,
      optionRenderer: option => <span style={{ fontWeight: option.isToggle ? 'bold' : 'normal' }}>{option.label}</span>,
      cellRenderer: apartment => {
        const isUpdating = selectedApartment?._id === apartment?._id && selectedApartment?.showProgress;
        const handleStatusChange = status => {
          setSelectedApartment({
            _id: apartment?._id,
            status,
            oldStatus: apartment?.status,
            currentApartmentArchivedStatus: apartment?.isApartmentArchived || false,
            updateKey: 'status'
          });
        };

        return (
          <ApartmentColEditStatusDropdown
            apartment={apartment}
            handleStatusChange={handleStatusChange}
            translate={translate}
            isUpdating={isUpdating}
          />
        );
      }
    },
    {
      key: 'type',
      label: 'Apartment Type',
      valueSelector: app => apartmentTypes?.find(a => a?.value === app?.type)?.label || '',
      width: 150,
      type: 'text',
      searchOptions: apartmentTypes,
      isMultiSelect: false
    },
    {
      key: 'reportId',
      label: 'Report',
      width: 150,
      type: 'exists',
      cellRenderer: apartment =>
        apartment?.reportId?._id ? (
          <Badge style={{ borderRadius: '5px' }} bg={apartment?.reportId?.status === 'complete' ? 'success' : 'gray'}>
            <FileEarmarkCheck size={10} /> {translate('report')}
          </Badge>
        ) : (
          '-'
        ),
      searchOptions: yesNoOpt,
      isMultiSelect: false,
      disableSort: true
    },
    { label: translate('buying_price'), key: 'inputPrice', type: 'number', width: 150 },
    {
      label: translate('value_of_received_apartment_today'),
      key: 'valueOfApartmentToday',
      type: 'number',
      width: 150
    },
    {
      key: 'irr',
      label: 'IRR',
      type: 'number',
      cellRenderer: app => {
        return <div>{formatPercentage(app?.irr * 100, true, true, app?.irr * 100 > 0 ? 2 : 0)}</div>;
      },
      sortable: true,
      width: 150
    },
    {
      key: 'customer',
      label: 'Customers',
      cellRenderer: app => {
        // Helper function to get background color based on status
        const getStatusColor = status => {
          const statusOption = profileAssignmentStatusOptions.find(option => option.value === status);
          return statusOption ? statusOption.selectedBgColor : '#6c757d'; // default gray
        };

        return app?.profileAssignments?.length ? (
          <>
            {app?.profileAssignments?.map((c, i) => (
              <div
                key={c?.customer?._id}
                className={`d-flex justify-content-center align-items-center text-center text-dark py-2 ${
                  i + 1 !== app.profileAssignments.length ? 'border-bottom' : ''
                }`}
              >
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <div className="text-start" style={{ maxWidth: 320 }}>
                        <div className="fw-bold">{c?.status || 'Unknown Status'}</div>
                        {c?.status === "Didn't Close" && c?.reason ? (
                          <div
                            className="small opacity-75 mt-1"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word'
                            }}
                          >
                            {c?.reason}
                          </div>
                        ) : null}
                      </div>
                    </Tooltip>
                  }
                >
                  <div
                    className="rounded-circle mx-2"
                    style={{
                      width: '10px',
                      height: '10px',
                      backgroundColor: getStatusColor(c?.status),
                      flexShrink: 0
                    }}
                  />
                </OverlayTrigger>
                <h6 className="smaller fw-bold mb-0">{c?.customer?.name || c?.customer?.email || 'No name'}</h6>
              </div>
            ))}
          </>
        ) : (
          <div className="text-center">-</div>
        );
      },
      width: 200,

      disableSort: true
    },

    {
      key: 'profileAssignments.assignmentDate',
      label: 'Assignment Date',
      type: 'date',
      width: 180,
      cellRenderer: app => {
        const handleChange = (fieldKey, value, customer) => {
          const updatedProfileAssignments = app?.profileAssignments.map(pa => {
            if (pa?.customer?._id === customer?._id) {
              return {
                ...pa,
                [fieldKey]: value
              };
            }
            return pa;
          });
          setSelectedApartment({
            _id: app?._id,
            profileAssignments: updatedProfileAssignments,
            updateKey: 'dates'
          });
        };
        return (
          <div className="d-flex flex-column align-items-center text-center flex-wrap gap-1 my-1">
            {app?.profileAssignments.length > 0 ? (
              app?.profileAssignments.map((pa, i) => (
                <EditAppartmentDates
                  value={pa?.assignmentDate}
                  apartment={app}
                  fieldKey="assignmentDate"
                  handleChange={(fieldKey, val) => handleChange(fieldKey, val, pa?.customer)}
                  className={`${i + 1 !== app.profileAssignments.length ? 'border-bottom' : ''} ${
                    pa.assignmentDate ? '' : 'py-2'
                  }`}
                />
              ))
            ) : (
              <div className="text-center">-</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'profileAssignments.advancedCheckDate',
      label: translate('advanced_check_date'),
      type: 'date',
      width: 180,
      cellRenderer: app => {
        const handleChange = (fieldKey, value, customer) => {
          const updatedProfileAssignments = app?.profileAssignments.map(pa => {
            if (pa?.customer?._id === customer?._id) {
              return {
                ...pa,
                [fieldKey]: value
              };
            }
            return pa;
          });
          setSelectedApartment({
            _id: app?._id,
            profileAssignments: updatedProfileAssignments,
            updateKey: 'dates'
          });
        };
        return (
          <div className="d-flex flex-column align-items-center text-center flex-wrap gap-1 my-1">
            {app?.profileAssignments.length > 0 ? (
              app?.profileAssignments.map((pa, i) => (
                <EditAppartmentDates
                  value={pa?.advancedCheckDate}
                  apartment={app}
                  fieldKey="advancedCheckDate"
                  handleChange={(fieldKey, val) => handleChange(fieldKey, val, pa?.customer)}
                  className={`${i + 1 !== app.profileAssignments.length ? 'border-bottom' : ''} ${
                    pa.advancedCheckDate ? '' : 'py-2'
                  }`}
                />
              ))
            ) : (
              <div className="text-center">-</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'profileAssignments.contractPreparationDate',
      label: 'Preparing Contract',
      type: 'date',
      width: 180,
      cellRenderer: app => {
        const handleChange = (fieldKey, value, customer) => {
          const updatedProfileAssignments = app?.profileAssignments.map(pa => {
            if (pa?.customer?._id === customer?._id) {
              return {
                ...pa,
                [fieldKey]: value
              };
            }
            return pa;
          });
          setSelectedApartment({ _id: app?._id, profileAssignments: updatedProfileAssignments, updateKey: 'dates' });
        };
        return (
          <div className="d-flex flex-column align-items-center text-center flex-wrap gap-1 my-1">
            {app?.profileAssignments.length > 0 ? (
              app?.profileAssignments.map((pa, i) => (
                <EditAppartmentDates
                  value={pa?.contractPreparationDate}
                  apartment={app}
                  fieldKey="contractPreparationDate"
                  handleChange={(fieldKey, val) => handleChange(fieldKey, val, pa?.customer)}
                  className={`${i + 1 !== app.profileAssignments.length ? 'border-bottom' : ''} ${
                    pa.contractPreparationDate ? '' : 'py-2'
                  }`}
                />
              ))
            ) : (
              <div className="text-center">-</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'boughtDate',
      label: 'Bought Date',
      type: 'date',
      width: 180,
      cellRenderer: app => {
        const handleChange = (fieldKey, value) => {
          setSelectedApartment({ _id: app?._id, [fieldKey]: value, updateKey: 'dates' });
        };
        return (
          <EditAppartmentDates
            value={app?.boughtDate}
            apartment={app}
            fieldKey="boughtDate"
            handleChange={handleChange}
          />
        );
      }
    },
    {
      key: 'profileAssignments.didNotCloseDate',
      label: translate('did_not_close_date'),
      type: 'date',
      width: 180,
      cellRenderer: app => {
        const handleChange = (fieldKey, value, customer) => {
          const updatedProfileAssignments = app?.profileAssignments.map(pa => {
            if (pa?.customer?._id === customer?._id) {
              return {
                ...pa,
                [fieldKey]: value
              };
            }
            return pa;
          });
          setSelectedApartment({ _id: app?._id, profileAssignments: updatedProfileAssignments, updateKey: 'dates' });
        };
        return (
          <div className="d-flex flex-column align-items-center text-center flex-wrap gap-1 my-1">
            {app?.profileAssignments.length > 0 ? (
              app?.profileAssignments.map((pa, i) => (
                <EditAppartmentDates
                  value={pa?.didNotCloseDate}
                  apartment={app}
                  fieldKey="didNotCloseDate"
                  handleChange={(fieldKey, val) => handleChange(fieldKey, val, pa?.customer)}
                  className={`${i + 1 !== app.profileAssignments.length ? 'border-bottom' : ''} ${
                    pa.didNotCloseDate ? '' : 'py-2'
                  }`}
                />
              ))
            ) : (
              <div className="text-center">-</div>
            )}
          </div>
        );
      }
    },

    {
      key: 'gushTatHelka',
      label: 'Gush helka tat helka',
      type: 'text',
      width: 150,
      disableSort: true
    },
    {
      label: translate('area_in_square_meters'),
      key: 'areaInSquareMeters',
      type: 'number',
      apartmentProfileKey: 'smallApartment',
      width: 200
    },

    { label: translate('appraised_value'), key: 'appraisedValue', type: 'number', width: 150 },

    {
      label: translate('cost_of_time_to_delivery_after_buying'),
      key: 'costOfTimeToDeliveryAfterBuying',
      type: 'number',
      width: 200
    },
    { label: translate('monthly_rent'), key: 'monthlyRent', type: 'number', width: 150 },
    {
      label: translate('time_to_delivery_after_buying'),
      key: 'timeToDeliveryAfterBuyingInMonths',
      type: 'number',
      width: 200
    },
    {
      label: translate('holding_period'),
      key: 'realEstateHoldingPeriodInYears',
      type: 'number',
      width: 200
    },
    {
      label: translate('estimated_time_for_demolition'),
      key: 'estimatedTimeForDemolition',
      type: 'number',
      width: 200
    },
    {
      label: translate('estimated_time_for_finishing_contruction_from_demolition'),
      key: 'estimatedTimeForFinishingContruction',
      type: 'number',
      width: 300
    },
    {
      label: translate('required_cash_for_deal'),
      key: 'requiredCashForDeal',
      type: 'number',
      searchOptions: rangeOpt,
      width: 150
    },
    {
      label: translate('developer_loan'),
      key: 'developerLoan',
      type: 'switch',
      searchOptions: yesNoOpt,
      width: 100
    },
    {
      label: translate('linear_payment'),
      key: 'linearPayment',
      type: 'switch',
      searchOptions: yesNoOpt,
      width: 100
    },
    {
      label: 'developer_company_scale',
      key: 'developerCompanyScale',
      type: 'number',
      searchOptions: rangeOpt,
      width: 150
    },
    {
      label: translate('buying_decision_scale'),
      key: 'buyingDecisionScale',
      type: 'switch',
      searchOptions: yesNoOpt,
      width: 150
    },
    {
      label: translate('large_scale_renovation'),
      key: 'largeRenovation',
      type: 'switch',
      searchOptions: yesNoOpt,
      width: 150
    },
    {
      label: translate('early_stage_deals'),
      key: 'earlyDeal',
      type: 'switch',
      searchOptions: yesNoOpt,
      width: 100
    },
    {
      label: translate('has_3_floor_or_higher_without_elevator'),
      key: 'noElevator',
      type: 'switch',
      searchOptions: yesNoOpt,
      width: 200
    },
    {
      label: translate('complicated_deals'),
      key: 'complicatedDeals',
      type: 'text',
      cellRenderer: app => {
        return app?.complicatedDeals?.map(deal => translate(snakeCase(deal))).join(', ');
      },
      isMultiSelect: true,
      searchOptions: COMPLICATED_DEALS_OPTIONS.map(deal => ({ label: translate(snakeCase(deal)), value: deal })),
      width: 200
    },
    {
      label: translate('willingness_for_mixed_population'),
      key: 'mixedPopulation',
      type: 'text',
      searchOptions: yesNoOpt,
      width: 200
    }
  ];

  return allLabels;
};
